// ═══════════════════════════════════════════════════════════════
// DataExportSection — a mechanic's own data, on their own machine.
//
// Three things at once: the answer to "can I get my data out?", the
// accountant's spreadsheet, and the safety net under a destructive
// delete. Nothing here leaves the browser — the app already holds
// this data, so the export is assembled locally and saved directly.
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { getDocs } from 'firebase/firestore';
import { Download, FileSpreadsheet, FileJson, Loader2 } from 'lucide-react';
import { inspectionsCol } from './database.js';
import { useUserData } from './lib/use-user-data.js';
import { useBusinessProfile } from './lib/use-business-profile.js';
import { auth } from './firebase.js';
import { APP_VERSION } from './version.js';
import {
  customersCsv, vehiclesCsv, jobsCsv, recommendationsCsv,
  buildJsonBundle, exportSummary, downloadText, stamp,
} from './lib/export-data.js';

export default function DataExportSection() {
  // allCustomers, not customers: this export is the last copy before
  // "Delete forever", so it has to include archived people too.
  const { jobs, allCustomers: customers, vehicles, ready } = useUserData();
  const { profile } = useBusinessProfile();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  const customerById = Object.fromEntries(customers.map((c) => [c.id, c]));
  const vehicleById = Object.fromEntries(vehicles.map((v) => [v.id, v]));
  const summary = exportSummary({ customers, vehicles, jobs });

  function saved(name) {
    setLastSaved(name);
    setError(null);
  }

  function runCsv(kind) {
    setBusy(kind);
    setError(null);
    try {
      const d = stamp();
      if (kind === 'jobs') {
        const name = `bayless-jobs-${d}.csv`;
        downloadText(name, jobsCsv(jobs, { customerById, vehicleById }));
        saved(name);
      } else if (kind === 'customers') {
        const name = `bayless-customers-${d}.csv`;
        downloadText(name, customersCsv(customers));
        saved(name);
      } else if (kind === 'vehicles') {
        const name = `bayless-vehicles-${d}.csv`;
        downloadText(name, vehiclesCsv(vehicles, customerById));
        saved(name);
      } else if (kind === 'recommendations') {
        const name = `bayless-recommended-work-${d}.csv`;
        downloadText(name, recommendationsCsv(jobs, { customerById }));
        saved(name);
      }
    } catch (e) {
      console.error('Export failed:', e);
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }

  // The full bundle is the only one that needs a read: inspections
  // live in their own collection and aren't loaded for normal use.
  async function runJson() {
    setBusy('json');
    setError(null);
    try {
      const snap = await getDocs(inspectionsCol());
      const inspections = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const bundle = buildJsonBundle({
        customers, vehicles, jobs, inspections, profile,
        appVersion: APP_VERSION,
        userEmail: auth.currentUser?.email || '',
      });
      const name = `bayless-everything-${stamp()}.json`;
      downloadText(name, JSON.stringify(bundle, null, 2), 'application/json');
      saved(name);
    } catch (e) {
      console.error('Full export failed:', e);
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }

  const csvButtons = [
    { kind: 'jobs',            label: 'Jobs & money',      hint: `${summary.jobs} jobs — the one to hand your accountant` },
    { kind: 'customers',       label: 'Customers',         hint: `${summary.customers} customers` },
    { kind: 'vehicles',        label: 'Vehicles',          hint: `${summary.vehicles} vehicles` },
    { kind: 'recommendations', label: 'Recommended work',  hint: `${summary.recommendations} offered · ${summary.declined} declined` },
  ];

  return (
    <section className="settings-section settings-section-standalone">
      <div className="settings-section-head">
        <Download size={16} />
        <div>
          <h2>Your data</h2>
          <p className="muted small">
            It's yours, and you can take it whenever you like. Spreadsheets open in Excel or
            Google Sheets; the full backup keeps everything exactly as it's stored.
          </p>
        </div>
      </div>

      {!ready && <p className="muted small">Loading…</p>}

      {ready && (
        <>
          <p className="export-group-label">Spreadsheets</p>
          <div className="export-grid">
            {csvButtons.map((b) => (
              <button
                key={b.kind}
                type="button"
                className="export-btn"
                disabled={!!busy}
                onClick={() => runCsv(b.kind)}
              >
                {busy === b.kind
                  ? <Loader2 size={16} className="spin" />
                  : <FileSpreadsheet size={16} />}
                <span>
                  <strong>{b.label}</strong>
                  <span className="muted small">{b.hint}</span>
                </span>
              </button>
            ))}
          </div>

          <p className="export-group-label" style={{ marginTop: 16 }}>Everything</p>
          <button
            type="button"
            className="export-btn export-btn-wide"
            disabled={!!busy}
            onClick={runJson}
          >
            {busy === 'json'
              ? <Loader2 size={16} className="spin" />
              : <FileJson size={16} />}
            <span>
              <strong>Full backup (JSON)</strong>
              <span className="muted small">
                Customers, vehicles, jobs and every inspection — the whole picture
              </span>
            </span>
          </button>

          <p className="muted small export-note">
            Photos are saved as links rather than image files, so they keep working while
            your account does. To keep the pictures themselves, download them separately.
          </p>

          {lastSaved && (
            <p className="muted small export-saved">
              ✓ Saved <span className="mono">{lastSaved}</span> to your downloads.
            </p>
          )}
          {error && <p className="form-error">{error}</p>}
        </>
      )}
    </section>
  );
}
